import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Camera, Film, Eye, Upload, Check, X, ShoppingCart, Star, Zap, Heart,
  ChevronRight, Shield, Settings, Clock, Aperture
} from 'lucide-react'
import Polaroid from '../components/Polaroid'

const AFFILIATE_LINK = 'https://www.amazon.com/dp/B0G834TQB4?pd_rd_i=B0G834TQB4&pd_rd_w=ZULVk&content-id=amzn1.sym.8c2f9165-8e93-42a1-8313-73d3809141a2&pf_rd_p=8c2f9165-8e93-42a1-8313-73d3809141a2&pf_rd_r=EG9FB0JEPRQXSZC37AJ1&pd_rd_wg=TKxVO&pd_rd_r=cbf9241b-7219-465d-b4d7-5d61c38d22fa&sp_csd=d2lkZ2V0TmFtZT1zcF9kZXRhaWw&th=1&linkCode=ll1&tag=homeinhouse-20&linkId=0736188751f315773494f5e91f97c981&language=en_US&ref_=as_li_ss_tl'

const KODAK_AFFILIATE_LINK = 'https://www.amazon.com/KODAK-Recording-Simulation-Megapixels-Beginners/dp/B0FKBDT8FR?crid=X3F9LQ06EB1X&dib=eyJ2IjoiMSJ9.G0MDFabjJNmdTKtw8vbcOwhLAVtLbVcLh57r0dOUx2LvzeSxHR43Rv_OyxPqegZsdoKIfn-ygRQXQn5-LgtJKMMHzJsDDmmflLoLlWc1YDMq8v-sSAy9fp0qiHjo7R_T-4VVNYr_wRJuQhreifVl8VLHA4UY4xFjK_eaVOe9nOWcxVrokx1KaWZ65QgKqJSslvkaD9M7rWpghp4rpYCJB8Td6pprud4SGgrlnI69JmJsoYKG_fgsU1sT2L_juIlLnIRgPwXzf3-7yGotZU6GGc_M-xUXyqUThtyFS558YDs.6WELCPgeAOEMKNlg6RbVb0OiGwIUtFwAhWhPlO0f6Gg&dib_tag=se&keywords=kodak%2Bcamera&qid=1767863170&s=electronics&sprefix=kodak%2Bcharmer%2Celectronics%2C433&sr=1-1&th=1&linkCode=ll1&tag=homeinhouse-20&linkId=cad03399c5e08feaa378f5ab2278d26c&language=en_US&ref_=as_li_ss_tl'

const initialPhotos = [
  { id: 1, url: 'https://images.unsplash.com/photo-1551029122-05b93f33d73b?auto=format&fit=crop&w=400', artist: '@Skater_Dan', status: 'approved' },
  { id: 2, url: 'https://images.unsplash.com/photo-1526047932273-341f2a7631f9?auto=format&fit=crop&w=400', artist: 'Jenna V.', status: 'approved' },
  { id: 3, url: 'https://images.unsplash.com/photo-1493863641943-9b68992a8d07?auto=format&fit=crop&w=400', artist: 'Tokyo Nights', status: 'approved' },
  { id: 4, url: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=400', artist: '@Retro_Soul', status: 'approved' },
  { id: 5, url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&fit=crop', artist: 'Sarah Chen', status: 'approved' },
]

const specs = [
  { icon: Film, title: 'CCD Sensor', value: '1/1.7"', desc: 'Authentic vintage color science like early 2000s digital cameras.' },
  { icon: Aperture, title: 'Resolution', value: '1080p HD', desc: "Crystal clear with distinct texture that filters can't replicate." },
  { icon: Eye, title: 'Field of View', value: '130° Wide', desc: 'Capture more in every frame. Perfect for landscapes and groups.' },
  { icon: Zap, title: 'Weight', value: '26 grams', desc: 'Lighter than keys. Clip it anywhere — always ready to shoot.' },
  { icon: Heart, title: 'Battery', value: '200+ shots', desc: 'Full day of shooting on a single charge. No anxiety.' },
  { icon: Camera, title: 'Color Modes', value: '8 profiles', desc: 'Built-in vintage looks. Y2K, film, B&W — no editing needed.' }
]

export default function HomePage() {
  const [photos] = useState(initialPhotos)

  return (
    <>
      <Helmet>
        <title>G6 Thumb Camera - Mini Digital Camera with Retro Film Point and Shoot Style | G6 Optics</title>
        <meta name="description" content="The G6 thumb camera delivers authentic vintage photography in a 26g pocket digital camera. CCD sensor for film point and shoot style photos. Shop the g6 mini camera today." />
        <link rel="canonical" href="https://g6optics.com/" />
        <meta property="og:title" content="G6 Thumb Camera - Retro Mini Digital Camera" />
        <meta property="og:description" content="26g keychain camera with CCD sensor. Early 2000s digital point and shoot style photography." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section with Primary Keywords */}
      <section className="hero pixel-texture">
        <div className="container">
          <div className="hero-grid">
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="hero-badge retro-border-sm"><Star size={12} /> RETRO CCD SENSOR</span>

              <h1 className="hero-title">
                G6 Thumb Camera:
                <br />
                <span className="hero-title-accent">Small Frame. Massive Soul.</span>
              </h1>

              <p className="hero-desc">
                The <strong>G6 thumb camera</strong> weighs just 26 grams and delivers authentic 
                vintage photography with its CCD sensor. This <Link to="/mini-digital-camera">mini digital camera</Link> captures 
                the <Link to="/retro-mini-camera">film point and shoot aesthetic</Link> that filters simply can't replicate. 
                Perfect as a keychain camera or pocket digital camera for everyday adventures.
              </p>

              <div className="hero-stats">
                <div>
                  <div className="stat-value">26g</div>
                  <div className="stat-label">Weight</div>
                </div>
                <div>
                  <div className="stat-value">1080p</div>
                  <div className="stat-label">Video</div>
                </div>
                <div>
                  <div className="stat-value">130°</div>
                  <div className="stat-label">Wide</div>
                </div>
              </div>

              <div className="hero-ctas">
                <a
                  href={AFFILIATE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary retro-border"
                >
                  <ShoppingCart size={18} />
                  Shop G6 Camera on Amazon
                </a>
                <Link to="/g6-thumb-camera" className="btn btn-secondary retro-border">
                  <Eye size={18} />
                  Learn More
                </Link>
              </div>

              <div className="hero-trust">
                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={12} className="star" />
                  ))}
                </div>
                <span>4.8 Rating</span>
                <span>|</span>
                <span className="trust-item">
                  <Shield size={12} />
                  30-Day Returns
                </span>
              </div>
            </motion.div>

            <motion.div
              className="hero-image"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="hero-image-wrapper retro-border dither-overlay">
                <img
                  src="https://m.media-amazon.com/images/S/aplus-media-library-service-media/2fd90c99-ca76-4891-bb9c-0873e88691e9.__CR0,0,1940,1200_PT0_SX970_V1___.png"
                  alt="G6 Thumb Camera - retro mini digital camera in multiple colors"
                  width="970"
                  height="600"
                />
                <div className="floating-tag floating-tag-top retro-border-sm">
                  <Film size={12} />
                  CCD Sensor
                </div>
                <div className="floating-tag floating-tag-bottom retro-border-sm">
                  <Zap size={12} />
                  Ultra Light
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why G6 Section - SEO Content */}
      <section className="seo-content-section pixel-dots">
        <div className="container">
          <h2 className="section-title">Why Choose the G6 Mini Camera?</h2>
          <div className="seo-content-grid">
            <article className="seo-content-block">
              <h3>Authentic Film Point and Shoot Style Photography</h3>
              <p>
                Unlike smartphone filters that simulate vintage looks, the <strong>G6 digital camera</strong> uses 
                a genuine CCD sensor to capture images with natural grain, warm tones, and authentic light leaks. 
                This <Link to="/retro-mini-camera">retro camera</Link> aesthetic comes straight from the 
                hardware—no post-processing required. Photography enthusiasts seeking an 
                <em> early 2000s digital point and shoot</em> experience will appreciate how the G6 captures the soul 
                of analog photography in a modern, convenient package. Looking for the <a href={KODAK_AFFILIATE_LINK} target="_blank" rel="noopener noreferrer">Kodak retro camera</a>? Check it out too.
              </p>
            </article>
            <article className="seo-content-block">
              <h3>The Ultimate Pocket Digital Camera</h3>
              <p>
                At just 26 grams, the G6 is the lightest <Link to="/mini-digital-camera">tiny digital camera</Link> in 
                its class. Designed as a <strong>keychain camera</strong>, it clips onto your keys, bag, or lanyard 
                so you never miss a moment. This <em>thumb camera</em> proves that great photography doesn't require 
                bulky equipment. Whether you call it a pocket digital camera, mini digital camera, or toy camera, 
                the G6 delivers professional-grade retro aesthetics in an impossibly small form factor.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Specs Section */}
      <section id="specs" className="specs pixel-dots">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">G6 Thumb Camera Technical Specs</h2>
            <p className="section-desc">
              Everything you need in a <Link to="/mini-digital-camera">mini digital camera</Link> for authentic retro photography.
            </p>
          </motion.div>

          <div className="specs-grid">
            {specs.map((spec, i) => (
              <motion.div
                key={i}
                className="spec-card retro-border-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="spec-inner">
                  <div className="spec-icon retro-border-sm">
                    <spec.icon size={18} />
                  </div>
                  <div>
                    <div className="spec-value">{spec.value}</div>
                    <div className="spec-title">{spec.title}</div>
                    <p className="spec-desc">{spec.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery pixel-dots">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Sample Pictures Taken with G6 Thumb Camera</h2>
            <p className="section-desc">
              Real sample photos showcasing the G6 mini camera's authentic retro aesthetic and CCD sensor quality.
            </p>
          </motion.div>

          <div className="gallery-grid">
            <AnimatePresence mode="popLayout">
              {photos.map((photo) => (
                <Polaroid key={photo.id} photo={photo} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta pixel-texture">
        <div className="container">
          <motion.div
            className="cta-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="cta-badge retro-border-sm">
              <Heart size={12} style={{ fill: 'currentColor' }} />
              Loved by 10,000+ photographers
            </div>

            <h2 className="cta-title">
              Ready for Your <span className="cta-title-accent">G6 Thumb Camera</span>?
            </h2>

            <p className="cta-desc">
              Join thousands who've discovered the joy of authentic retro photography with the 
              G6 mini digital camera. Available in 9 colors on Amazon.
            </p>

            <a
              href={AFFILIATE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary retro-border"
            >
              <ShoppingCart size={18} />
              Buy G6 Camera on Amazon
              <ChevronRight size={18} />
            </a>

            <p className="cta-fine">Free shipping • 30-day returns • 1-year warranty</p>

            <div className="cta-links">
              <Link to="/faq">Have questions? Read our FAQ</Link>
              <span>|</span>
              <Link to="/retro-mini-camera">Compare to other retro cameras</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
